import type { Application } from 'pixi.js';

/**
 * Take a Pixi app down without harming any other Pixi app on the page, and
 * without anything throwing before its renderer (and its WebGL context) is
 * freed.
 *
 * <ul>
 *   <li>The stage goes first, while the app's ticker still runs. The app's
 *       own destroy takes the ticker down before the stage, so a widget that
 *       takes its listener off the ticker in its own destroy would throw
 *       there, and the throw would stop the renderer being freed. Phones
 *       allow only a few WebGL contexts.</li>
 *   <li>Then the app, with its canvas, as {@code destroy({ removeView: true })}.
 *       Never {@code destroy(true)}: in Pixi 8 that also releases the
 *       resources every renderer on the page shares, and any other canvas
 *       still running (the next scene, a board on its own canvas) then
 *       throws on every frame and stays blank.</li>
 * </ul>
 *
 * Errors are logged as "[label] destroy threw" rather than thrown: a
 * teardown carries on.
 */
export function destroyPixiApp(app: Application, label: string): void {
  try {
    // Before init has finished there is no stage yet.
    app.stage?.destroy({ children: true });
  } catch (err) {
    console.error(`[${label}] stage destroy threw`, err);
  }
  try {
    app.destroy({ removeView: true }, { children: true });
  } catch (err) {
    console.error(`[${label}] destroy threw`, err);
  }
}
